import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";

/**
 * Read-only detail modal for a single session record. Fetches full doc via
 * GET /api/admin/sessions/{id} on open. Shows all 12 pillar scores, attendance
 * numbers, Continuity, View, Comment, plus source/photo/transcription meta.
 *
 * Props:
 *   sessionId    string | null    controls open state (null = closed)
 *   onClose      () => void
 *   onDelete     (sessionId) => Promise<void>   optional — shows Delete button
 *   onRestore    (sessionId) => Promise<void>   optional — shows Restore button
 */
export default function SessionDetailDialog({ sessionId, onClose, onDelete, onRestore }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sessionId) { setSession(null); return; }
    setLoading(true);
    api.get(`/admin/sessions/${sessionId}`)
      .then((r) => setSession(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load session"))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const s = session;
  const isDeleted = s?.status === "Deleted";

  const P = ({ label, value }) => (
    <div className="text-sm">
      <span className="text-neutral-500">{label}:</span>{" "}
      <span className="text-neutral-900 font-medium">{value ?? "—"}</span>
    </div>
  );

  const ScoreRow = ({ label, value }) => (
    <div className="flex items-center justify-between py-1.5 border-b border-neutral-100 last:border-0">
      <span className="text-sm text-neutral-700">{label}</span>
      <span className="font-semibold text-neutral-900 w-8 text-right">{value ?? "—"}</span>
    </div>
  );

  return (
    <Dialog open={!!sessionId} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="session-detail-dialog">
        <DialogHeader>
          <DialogTitle>Session Detail</DialogTitle>
          <DialogDescription>
            Read-only view of the full session record.
          </DialogDescription>
        </DialogHeader>

        {loading || !s ? (
          <div className="flex items-center justify-center py-10 text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-5">
            {isDeleted && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900" data-testid="detail-deleted-banner">
                This session is in <b>Recently Deleted</b> ({s.deleted_at ? new Date(s.deleted_at).toLocaleString() : "unknown time"}). It is excluded from lists, exports, and averages until restored.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <P label="School" value={`${s.school_name}${s.school_type ? ` · ${s.school_type}` : ""}`} />
              <P label="Year Level · Class" value={`${s.year_level} · ${s.class_name}`} />
              <P label="Teacher · Role" value={`${s.teacher_name} · ${s.role}`} />
              <P label="Term · Session/Week" value={`${s.term} · ${s.session_week}`} />
              <P label="Date" value={s.date} />
              <P label="Programme" value={s.programme_type} />
              <P label="Source" value={s.source === "paper_transcription" ? "Paper (transcribed)" : "Digital"} />
              <P label="Submitted" value={s.created_at ? new Date(s.created_at).toLocaleString() : "—"} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="font-display text-base font-semibold text-neutral-900 mb-2">
                  Engagement <span className="text-neutral-500 text-sm font-normal">/25</span>
                </div>
                <ScoreRow label="Listening" value={s.scores?.listening} />
                <ScoreRow label="Attempting" value={s.scores?.attempting} />
                <ScoreRow label="On Task" value={s.scores?.onTask} />
                <ScoreRow label="Responding" value={s.scores?.responding} />
                <div className="mt-2 text-right text-sm">
                  Total: <b data-testid="detail-eng-total">{s.engagement_score}</b>/25
                </div>
              </div>
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="font-display text-base font-semibold text-neutral-900 mb-2">
                  Behaviour <span className="text-neutral-500 text-sm font-normal">/25</span>
                </div>
                <ScoreRow label="Safe" value={s.scores?.safe} />
                <ScoreRow label="Respectful" value={s.scores?.respectful} />
                <ScoreRow label="Managing" value={s.scores?.managing} />
                <ScoreRow label="Adjusting" value={s.scores?.adjusting} />
                <div className="mt-2 text-right text-sm">
                  Total: <b data-testid="detail-beh-total">{s.behaviour_score}</b>/25
                </div>
              </div>
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="font-display text-base font-semibold text-neutral-900 mb-2">
                  Collective Energy <span className="text-neutral-500 text-sm font-normal">/25</span>
                </div>
                <ScoreRow label="Effort" value={s.scores?.effort} />
                <ScoreRow label="Encouraging" value={s.scores?.encouraging} />
                <ScoreRow label="Together" value={s.scores?.together} />
                <ScoreRow label="Uplifting" value={s.scores?.uplifting} />
                <div className="mt-2 text-right text-sm">
                  Total: <b data-testid="detail-ce-total">{s.collective_energy_score}</b>/25
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-neutral-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-neutral-500">Attendance</div>
                <div className="text-lg text-neutral-900 font-semibold" data-testid="detail-attendance">
                  {s.present_learners} present of {s.expected_on_roll} expected
                  {s.attendance_percent != null && ` · ${s.attendance_percent}%`}
                </div>
                {s.attendance_status !== "Valid" && (
                  <div className="text-xs text-amber-800 mt-1">{s.attendance_status}</div>
                )}
              </div>
              <div>
                <div className="text-sm text-neutral-500">CPI Total</div>
                <div className={`text-2xl font-bold ${s.cpi_total == null ? "text-neutral-400" : "text-[#2D5A4F]"}`} data-testid="detail-cpi-total">
                  {s.cpi_total == null ? "N/A" : `${s.cpi_total}/100`}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="text-xs text-neutral-500 mb-1">Session Continuity</div>
                <div className="text-sm font-medium text-neutral-900">{s.continuity || "—"}</div>
              </div>
              <div className="rounded-lg border border-neutral-200 p-4">
                <div className="text-xs text-neutral-500 mb-1">Session View</div>
                <div className="text-sm font-medium text-neutral-900">{s.session_view || "—"}</div>
              </div>
            </div>

            <div className="rounded-lg border border-neutral-200 p-4">
              <div className="text-xs text-neutral-500 mb-1">Collective Comment</div>
              <div className="text-sm text-neutral-800 whitespace-pre-wrap" data-testid="detail-comment">
                {s.comment || <span className="text-neutral-400">— none —</span>}
              </div>
            </div>

            {s.source === "paper_transcription" && s.transcribed_note && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="text-xs text-amber-800 mb-1">Transcription note</div>
                <div className="text-sm text-amber-900 whitespace-pre-wrap">{s.transcribed_note}</div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              {isDeleted && onRestore && (
                <button
                  onClick={async () => { await onRestore(s.id); onClose?.(); }}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-neutral-300 bg-white text-sm font-medium text-neutral-800 hover:bg-neutral-50"
                  data-testid="detail-btn-restore"
                >
                  <RotateCcw className="w-3.5 h-3.5" /> Restore
                </button>
              )}
              {!isDeleted && onDelete && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Soft-delete this session? It will be excluded from lists/exports/averages but recoverable from Recently Deleted.")) return;
                    await onDelete(s.id);
                    onClose?.();
                  }}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-red-300 bg-white text-sm font-medium text-red-800 hover:bg-red-50"
                  data-testid="detail-btn-delete"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
